// React Compiler opt-out: reads getHeaderGroups()/sort state off the mutable,
// stable-identity `table` instance prop — compiled caching would freeze sort
// indicators. The incompatible-library bailout only covers DataTable.tsx itself.
'use no memo';

import React from 'react';
import {flexRender} from '@tanstack/react-table';
import classnames from 'classnames';

import Text from '../../../atoms/Text/Text.react';
import Checkbox from '../../../atoms/Checkbox/Checkbox.react';
import IconButton from '../../../molecules/IconButton/IconButton.react';

import './data-table.scss';
import type {DataTableHeaderProps} from './DataTable.types';

/**
 * Renders the `<thead>` for DataTable.
 *
 */
function DataTableHeader<TData>({
  table,
  headerless,
  enableRowSelection
}: DataTableHeaderProps<TData>) {
  if (headerless) {
    return null;
  }

  return (
    <thead>
      {table.getHeaderGroups().map((headerGroup) => (
        <tr className='data-table__row' key={headerGroup.id}>
          {/* Select-all checkbox */}
          {enableRowSelection && (
            <th className='data-table__header data-table__selection-header'>
              <Checkbox
                label='Select all rows'
                className='data-table__row-select'
                checked={table.getIsAllRowsSelected()}
                onClick={table.getToggleAllRowsSelectedHandler()}
                size='s'
              />
            </th>
          )}

          {headerGroup.headers.map((header) => {
            const canSort = header.column.getCanSort();
            const sortDirection = header.column.getIsSorted(); // 'asc' | 'desc' | false
            const align = (header.column.columnDef.meta as any)?.align;
            const isActionMenu = (header.column.columnDef.meta as any)?.isActionMenu;
            const headerClassName = (header.column.columnDef.meta as any)?.className;

            return (
              <th
                key={header.id}
                className={classnames('data-table__header', headerClassName, {
                  [`data-table__header--${align}`]: align,
                  'data-table__action-menu-header': isActionMenu
                })}
                style={{
                  ...(header.getSize() !== 150 ? {width: header.getSize()} : {}),
                  ...(header.column.columnDef.minSize
                    ? {minWidth: header.column.columnDef.minSize}
                    : {})
                }}
              >
                <div
                  className={classnames('data-table__header-content', {
                    'flex justify-between': canSort
                  })}
                >
                  {header.isPlaceholder
                    ? null
                    : (() => {
                        const rendered = flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        );
                        return typeof rendered === 'string' ? (
                          <Text className='data-table__header-text' size='m' bold>
                            {rendered}
                          </Text>
                        ) : (
                          rendered
                        );
                      })()}
                  {canSort && (
                    <IconButton
                      color={sortDirection ? 'primary' : 'secondary'}
                      className='data-table__header-sort'
                      label='sort'
                      size='s'
                      icon={`arrow-${sortDirection === 'desc' ? 'down' : 'up'}`}
                      onClick={header.column.getToggleSortingHandler()}
                    />
                  )}
                </div>
              </th>
            );
          })}
        </tr>
      ))}
    </thead>
  );
}

export default DataTableHeader;
